import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import {
  AgenciesResponse,
  Agency,
  AgencyRoleFilter,
  PaginationMetadata,
} from '../models/agency.model';
import { AgentSession, AgentSessionResponse } from '../models/agent-session.model';

// The endpoint's documented ceiling. Requesting the max keeps the common
// case (an agent in a handful of agencies) to a single round trip.
const AGENCIES_PAGE_SIZE = 50;

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly bpBackendUrl = environment.bpBackendUrl;

  constructor(private http: HttpClient) {}

  // Replaces the legacy `${apiUrl}/v2.0/agent/account/info`. The agency
  // scope comes from the Ag-Agency header, which authInterceptor sets
  // from localStorage — omitted on first login, so the backend falls back
  // to the agent's default agency.
  getAgentSession(): Observable<AgentSession> {
    const url = `${this.bpBackendUrl}/agent/session`;

    return this.http.get<AgentSessionResponse>(url).pipe(map(response => response.data));
  }

  // Replaces the legacy `${apiUrl}/v2.0/agent/account/agencies`.
  //
  // Two differences that matter to callers:
  //
  //   1. The response carries no `role` field, so the caller can no longer
  //      filter by role client-side — pass it here and the backend filters.
  //   2. The response is paginated. The agency switcher renders a plain
  //      scrolling list with no pager, so a truncated result would silently
  //      hide agencies the agent can reach. We follow every page and
  //      concatenate, keeping the "whole list" contract the old endpoint had.
  getAgencies(role?: AgencyRoleFilter): Observable<Agency[]> {
    return this.getAgenciesPage(1, role).pipe(
      switchMap(first => {
        const firstPage = first.data ?? [];
        const rest = this.remainingPages(first.metadata);
        if (!rest.length) {
          return of(firstPage);
        }

        return forkJoin(
          rest.map(page =>
            this.getAgenciesPage(page, role).pipe(
              // Don't let one failed page discard the ones that succeeded —
              // forkJoin errors the whole stream on any inner failure, which
              // would blank the switcher instead of showing a short list.
              // A page-1 failure still errors out: that's a real failure the
              // caller should see.
              catchError(err => {
                console.error(`Failed to load agencies page ${page} of ${rest.length + 1}`, err);
                return of<AgenciesResponse>({ data: [], metadata: first.metadata });
              })
            )
          )
        ).pipe(map(pages => firstPage.concat(...pages.map(p => p.data ?? []))));
      })
    );
  }

  private getAgenciesPage(page: number, role?: AgencyRoleFilter): Observable<AgenciesResponse> {
    const url = `${this.bpBackendUrl}/agent/agencies`;
    const params: Record<string, string | number> = { limit: AGENCIES_PAGE_SIZE, page };
    if (role) {
      params['role'] = role;
    }

    return this.http.get<AgenciesResponse>(url, { params });
  }

  // Page numbers after the first, [2..total_pages]. Empty when the first
  // response already holds everything, which is the usual case. `metadata`
  // is required by the spec, but defaulting to a single page keeps a
  // malformed body from throwing inside the switchMap and blanking the
  // switcher — we'd rather render page 1 than nothing.
  private remainingPages(metadata?: PaginationMetadata): number[] {
    const totalPages = metadata?.total_pages ?? 1;
    const pages: number[] = [];
    for (let page = 2; page <= totalPages; page++) {
      pages.push(page);
    }
    return pages;
  }
}
